/**
 * Accessibility Utilities
 * Following constitution requirements for ArbiWin platform
 *
 * This file provides ARIA helpers, keyboard navigation,
 * and accessibility utilities for WCAG 2.1 AA compliance.
 */

import { browserHelpers } from './helpers';

/**
 * ARIA Utilities
 */
export const ariaHelpers = {
  /**
   * Generate unique ARIA ID
   * @param {string} prefix - ID prefix
   * @returns {string} Unique ARIA ID
   */
  generateAriaId: function(prefix) {
    prefix = prefix || 'aria';
    return prefix + '-' + Date.now() + '-' + Math.random().toString(36).substr(2, 5);
  },

  /**
   * Create ARIA label for surebet data
   * @param {Object} surebetData - Surebet data object
   * @param {string} language - Language code (bg/en)
   * @returns {string} ARIA label
   */
  createSurebetAriaLabel: function(surebetData, language) {
    if (!surebetData || !surebetData.event) return '';

    language = language || 'bg';
    const event = surebetData.event;

    if (language === 'bg') {
      return 'Арбитражна възможност: ' + event.teamOne + ' срещу ' + event.teamTwo +
             ', дата ' + event.date + ', час ' + event.time +
             ', първенство ' + event.championship;
    } else {
      return 'Arbitrage opportunity: ' + event.teamOne + ' versus ' + event.teamTwo +
             ', date ' + event.date + ', time ' + event.time +
             ', championship ' + event.championship;
    }
  },

  /**
   * Create ARIA label for margin data
   * @param {Object} marginData - Margin data object
   * @param {string} marginType - Margin type
   * @param {string} language - Language code (bg/en)
   * @returns {string} ARIA label
   */
  createMarginAriaLabel: function(marginData, marginType, language) {
    if (!marginData) return '';

    language = language || 'bg';
    const percentage = (marginData.value * 100).toFixed(2);

    const marginTypeNames = {
      'margin1X2': { bg: 'Краен резултат', en: 'Match Result' },
      'marginOverUnder2AndHalf': { bg: 'Над под 2.5 гола', en: 'Over Under 2.5 Goals' },
      'marginBothScore': { bg: 'Двата отбора ще вкарат', en: 'Both Teams to Score' },
      'margin1Xvs2': { bg: '1X срещу 2', en: '1X vs 2' },
      'marginX2vs1': { bg: 'X2 срещу 1', en: 'X2 vs 1' },
      'margin12vsX': { bg: '12 срещу X', en: '12 vs X' }
    };

    const typeName = marginTypeNames[marginType] ? marginTypeNames[marginType][language] : marginType;

    if (language === 'bg') {
      return typeName + ', марж ' + percentage + ' процента' +
             (marginData.value > 0 ? ', печеливша възможност' : ', не е печеливша');
    } else {
      return typeName + ', margin ' + percentage + ' percent' +
             (marginData.value > 0 ? ', profitable opportunity' : ', not profitable');
    }
  },

  /**
   * Create ARIA label for calculator
   * @param {Object} calculationData - Calculator data
   * @param {string} language - Language code (bg/en)
   * @returns {string} ARIA label
   */
  createCalculatorAriaLabel: function(calculationData, language) {
    if (!calculationData) return '';

    language = language || 'bg';

    if (language === 'bg') {
      return 'Калкулатор за арбитражен залог, обща сума ' + calculationData.totalStake + ' евро' +
             (calculationData.profit ? ', очаквана възвръщаемост ' + calculationData.profit + ' евро' : '');
    } else {
      return 'Arbitrage calculator, total stake ' + calculationData.totalStake + ' EUR' +
             (calculationData.profit ? ', expected return ' + calculationData.profit + ' EUR' : '');
    }
  },

  /**
   * Set ARIA live region content
   * @param {string} message - Message to announce
   * @param {string} priority - Priority level ('polite' or 'assertive')
   */
  announceToScreenReader: function(message, priority) {
    if (!browserHelpers.isBrowser() || !message) return;

    priority = priority || 'polite';

    // Find or create live region
    const liveRegionId = 'arbiwin-live-region';
    let liveRegion = document.getElementById(liveRegionId);

    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.id = liveRegionId;
      liveRegion.setAttribute('aria-live', priority);
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.style.position = 'absolute';
      liveRegion.style.left = '-10000px';
      liveRegion.style.width = '1px';
      liveRegion.style.height = '1px';
      liveRegion.style.overflow = 'hidden';

      document.body.appendChild(liveRegion);
    }

    // Clear and set new message
    liveRegion.textContent = '';
    setTimeout(function() {
      liveRegion.textContent = message;
    }, 100);
  }
};

/**
 * Keyboard Navigation Utilities
 */
export const keyboardHelpers = {
  /**
   * Key codes for common keys
   */
  KEYS: {
    ENTER: 13,
    ESCAPE: 27,
    SPACE: 32,
    ARROW_UP: 38,
    ARROW_DOWN: 40,
    ARROW_LEFT: 37,
    ARROW_RIGHT: 39,
    TAB: 9,
    HOME: 36,
    END: 35,
    PAGE_UP: 33,
    PAGE_DOWN: 34
  },

  /**
   * Check if key matches expected key
   * @param {KeyboardEvent} event - Keyboard event
   * @param {number|string} expectedKey - Expected key code or key string
   * @returns {boolean} True if key matches
   */
  isKey: function(event, expectedKey) {
    if (!event) return false;

    if (typeof expectedKey === 'number') {
      return event.keyCode === expectedKey || event.which === expectedKey;
    }

    if (typeof expectedKey === 'string') {
      return event.key === expectedKey || event.code === expectedKey;
    }

    return false;
  },

  /**
   * Handle keyboard navigation for lists
   * @param {KeyboardEvent} event - Keyboard event
   * @param {Array} items - Array of focusable items
   * @param {number} currentIndex - Current focused index
   * @param {Function} onIndexChange - Callback for index change
   */
  handleListNavigation: function(event, items, currentIndex, onIndexChange) {
    if (!event || !Array.isArray(items) || !onIndexChange) return;

    let newIndex = currentIndex;

    if (this.isKey(event, this.KEYS.ARROW_DOWN)) {
      event.preventDefault();
      newIndex = Math.min(currentIndex + 1, items.length - 1);
    } else if (this.isKey(event, this.KEYS.ARROW_UP)) {
      event.preventDefault();
      newIndex = Math.max(currentIndex - 1, 0);
    } else if (this.isKey(event, this.KEYS.HOME)) {
      event.preventDefault();
      newIndex = 0;
    } else if (this.isKey(event, this.KEYS.END)) {
      event.preventDefault();
      newIndex = items.length - 1;
    }

    if (newIndex !== currentIndex) {
      onIndexChange(newIndex);
    }
  },

  /**
   * Handle modal keyboard navigation
   * @param {KeyboardEvent} event - Keyboard event
   * @param {Function} onClose - Close modal callback
   * @param {Function} onConfirm - Confirm action callback
   */
  handleModalNavigation: function(event, onClose, onConfirm) {
    if (!event) return;

    if (this.isKey(event, this.KEYS.ESCAPE) && onClose) {
      event.preventDefault();
      onClose();
    } else if (this.isKey(event, this.KEYS.ENTER) && onConfirm) {
      event.preventDefault();
      onConfirm();
    }
  },

  /**
   * Trap focus within element
   * @param {HTMLElement} element - Element to trap focus within
   * @returns {Function} Cleanup function
   */
  trapFocus: function(element) {
    if (!browserHelpers.isBrowser() || !element) {
      return function() {};
    }

    const focusableElements = element.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    function handleTabKey(event) {
      if (!keyboardHelpers.isKey(event, keyboardHelpers.KEYS.TAB)) return;

      if (event.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    }

    element.addEventListener('keydown', handleTabKey);

    // Focus first element
    if (firstElement) {
      firstElement.focus();
    }

    // Return cleanup function
    return function() {
      element.removeEventListener('keydown', handleTabKey);
    };
  }
};

/**
 * Focus Management Utilities
 */
export const focusHelpers = {
  /**
   * Set focus to element with error handling
   * @param {HTMLElement|string} elementOrSelector - Element or selector
   * @param {number} delay - Optional delay before focusing
   * @returns {boolean} True if focus was set
   */
  setFocus: function(elementOrSelector, delay) {
    if (!browserHelpers.isBrowser()) return false;

    const element = typeof elementOrSelector === 'string'
      ? document.querySelector(elementOrSelector)
      : elementOrSelector;

    if (!element || typeof element.focus !== 'function') {
      return false;
    }

    const focusElement = function() {
      try {
        element.focus();
        return true;
      } catch (error) {
        console.warn('Focus failed:', error);
        return false;
      }
    };

    if (delay && delay > 0) {
      setTimeout(focusElement, delay);
      return true;
    } else {
      return focusElement();
    }
  },

  /**
   * Get currently focused element
   * @returns {HTMLElement|null} Currently focused element
   */
  getFocusedElement: function() {
    if (!browserHelpers.isBrowser()) return null;
    return document.activeElement;
  },

  /**
   * Check if element is focusable
   * @param {HTMLElement} element - Element to check
   * @returns {boolean} True if element is focusable
   */
  isFocusable: function(element) {
    if (!element) return false;

    const focusableSelectors = [
      'button',
      'input',
      'select',
      'textarea',
      'a[href]',
      '[tabindex]'
    ];

    const isFocusableElement = focusableSelectors.some(function(selector) {
      return element.matches(selector);
    });

    if (!isFocusableElement) return false;

    // Check if element is disabled or has negative tabindex
    if (element.disabled || element.tabIndex < 0) return false;

    // Check if element is visible
    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden') return false;

    return true;
  },

  /**
   * Get all focusable elements within container
   * @param {HTMLElement} container - Container element
   * @returns {Array} Array of focusable elements
   */
  getFocusableElements: function(container) {
    if (!container) return [];

    const focusableSelectors = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const elements = Array.from(container.querySelectorAll(focusableSelectors));

    return elements.filter(function(element) {
      return focusHelpers.isFocusable(element);
    });
  }
};

/**
 * Screen Reader Utilities
 */
export const screenReaderHelpers = {
  /**
   * Create screen reader only text
   * @param {string} text - Text for screen readers
   * @returns {HTMLElement} Hidden element with text
   */
  createScreenReaderText: function(text) {
    if (!browserHelpers.isBrowser()) return null;

    const element = document.createElement('span');
    element.textContent = text;
    element.className = 'sr-only';
    element.style.position = 'absolute';
    element.style.width = '1px';
    element.style.height = '1px';
    element.style.padding = '0';
    element.style.margin = '-1px';
    element.style.overflow = 'hidden';
    element.style.clip = 'rect(0, 0, 0, 0)';
    element.style.whiteSpace = 'nowrap';
    element.style.border = '0';

    return element;
  },

  /**
   * Announce status change to screen readers
   * @param {string} message - Status message
   * @param {string} language - Language code (bg/en)
   * @param {string} priority - Priority ('polite' or 'assertive')
   */
  announceStatus: function(message, language, priority) {
    if (!message) return;

    language = language || 'bg';
    priority = priority || 'polite';

    ariaHelpers.announceToScreenReader(message, priority);
  },

  /**
   * Announce surebet data update
   * @param {number} count - Number of surebets
   * @param {string} language - Language code (bg/en)
   */
  announceSurebetUpdate: function(count, language) {
    language = language || 'bg';

    let message;
    if (language === 'bg') {
      if (count === 0) {
        message = 'Няма налични арбитражни възможности';
      } else if (count === 1) {
        message = 'Намерена е 1 арбитражна възможност';
      } else {
        message = 'Намерени са ' + count + ' арбитражни възможности';
      }
    } else {
      if (count === 0) {
        message = 'No arbitrage opportunities available';
      } else if (count === 1) {
        message = '1 arbitrage opportunity found';
      } else {
        message = count + ' arbitrage opportunities found';
      }
    }

    this.announceStatus(message, language, 'polite');
  },

  /**
   * Announce calculator result
   * @param {Object} result - Calculator result
   * @param {string} language - Language code (bg/en)
   */
  announceCalculatorResult: function(result, language) {
    if (!result) return;

    language = language || 'bg';

    let message;
    if (language === 'bg') {
      message = 'Изчисление завършено. Очаквана възвръщаемост ' +
                result.profit + ' евро от общ залог ' + result.totalStake + ' евро';
    } else {
      message = 'Calculation completed. Expected return ' +
                result.profit + ' EUR from total stake ' + result.totalStake + ' EUR';
    }

    this.announceStatus(message, language, 'assertive');
  }
};

/**
 * Color Contrast Utilities
 */
export const contrastHelpers = {
  /**
   * Calculate color contrast ratio
   * @param {string} color1 - First color (hex)
   * @param {string} color2 - Second color (hex)
   * @returns {number} Contrast ratio
   */
  calculateContrastRatio: function(color1, color2) {
    const luminance1 = this.getLuminance(color1);
    const luminance2 = this.getLuminance(color2);

    const lighter = Math.max(luminance1, luminance2);
    const darker = Math.min(luminance1, luminance2);

    return (lighter + 0.05) / (darker + 0.05);
  },

  /**
   * Get relative luminance of color
   * @param {string} color - Color (hex)
   * @returns {number} Relative luminance
   */
  getLuminance: function(color) {
    const rgb = this.hexToRgb(color);
    if (!rgb) return 0;

    const rsRGB = rgb.r / 255;
    const gsRGB = rgb.g / 255;
    const bsRGB = rgb.b / 255;

    const r = rsRGB <= 0.03928 ? rsRGB / 12.92 : Math.pow((rsRGB + 0.055) / 1.055, 2.4);
    const g = gsRGB <= 0.03928 ? gsRGB / 12.92 : Math.pow((gsRGB + 0.055) / 1.055, 2.4);
    const b = bsRGB <= 0.03928 ? bsRGB / 12.92 : Math.pow((bsRGB + 0.055) / 1.055, 2.4);

    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  },

  /**
   * Convert hex color to RGB
   * @param {string} hex - Hex color (e.g., '#ff0000')
   * @returns {Object|null} RGB object or null
   */
  hexToRgb: function(hex) {
    if (!hex || typeof hex !== 'string') return null;

    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  },

  /**
   * Check if color combination meets WCAG AA standards
   * @param {string} foreground - Foreground color
   * @param {string} background - Background color
   * @param {boolean} isLargeText - True if text is large (18pt+ or 14pt+ bold)
   * @returns {Object} Compliance result
   */
  checkWCAGCompliance: function(foreground, background, isLargeText) {
    const ratio = this.calculateContrastRatio(foreground, background);
    const requiredRatio = isLargeText ? 3 : 4.5;

    return {
      ratio: ratio,
      requiredRatio: requiredRatio,
      passes: ratio >= requiredRatio,
      level: ratio >= 7 ? 'AAA' : ratio >= requiredRatio ? 'AA' : 'fail'
    };
  }
};

/**
 * Reduced Motion Utilities
 */
export const motionHelpers = {
  /**
   * Check if user prefers reduced motion
   * @returns {boolean} True if reduced motion preferred
   */
  prefersReducedMotion: function() {
    if (!browserHelpers.isBrowser()) return false;

    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  },

  /**
   * Get animation duration based on motion preference
   * @param {number} normalDuration - Normal animation duration
   * @param {number} reducedDuration - Reduced animation duration
   * @returns {number} Appropriate duration
   */
  getAnimationDuration: function(normalDuration, reducedDuration) {
    if (this.prefersReducedMotion()) {
      return reducedDuration || 0;
    }

    return normalDuration;
  },

  /**
   * Apply motion-safe styles
   * @param {HTMLElement} element - Element to apply styles to
   * @param {Object} normalStyles - Normal animation styles
   * @param {Object} reducedStyles - Reduced motion styles
   */
  applyMotionSafeStyles: function(element, normalStyles, reducedStyles) {
    if (!element) return;

    const styles = this.prefersReducedMotion() ? reducedStyles : normalStyles;

    Object.keys(styles).forEach(function(property) {
      element.style[property] = styles[property];
    });
  }
};

/**
 * High Contrast Mode Utilities
 */
export const contrastModeHelpers = {
  /**
   * Check if high contrast mode is enabled
   * @returns {boolean} True if high contrast mode is active
   */
  isHighContrastMode: function() {
    if (!browserHelpers.isBrowser()) return false;

    // Check for Windows high contrast mode
    return window.matchMedia && (
      window.matchMedia('(-ms-high-contrast: active)').matches ||
      window.matchMedia('(forced-colors: active)').matches
    );
  },

  /**
   * Apply high contrast styles
   * @param {HTMLElement} element - Element to apply styles to
   * @param {Object} highContrastStyles - High contrast styles
   */
  applyHighContrastStyles: function(element, highContrastStyles) {
    if (!element || !this.isHighContrastMode()) return;

    Object.keys(highContrastStyles).forEach(function(property) {
      element.style[property] = highContrastStyles[property];
    });
  }
};

/**
 * Accessibility Testing Utilities
 */
export const a11yTesting = {
  /**
   * Check element for common accessibility issues
   * @param {HTMLElement} element - Element to check
   * @returns {Array} Array of accessibility issues
   */
  checkAccessibility: function(element) {
    if (!element) return [];

    const issues = [];

    // Check for missing alt text on images
    if (element.tagName === 'IMG' && !element.alt) {
      issues.push({
        type: 'missing-alt',
        message: 'Image missing alt attribute',
        element: element
      });
    }

    // Check for missing labels on form controls
    if (['INPUT', 'SELECT', 'TEXTAREA'].includes(element.tagName)) {
      const hasLabel = element.labels && element.labels.length > 0;
      const hasAriaLabel = element.getAttribute('aria-label');
      const hasAriaLabelledBy = element.getAttribute('aria-labelledby');

      if (!hasLabel && !hasAriaLabel && !hasAriaLabelledBy) {
        issues.push({
          type: 'missing-label',
          message: 'Form control missing accessible label',
          element: element
        });
      }
    }

    // Check for missing ARIA roles on interactive elements
    if (element.onclick && !element.getAttribute('role') && element.tagName === 'DIV') {
      issues.push({
        type: 'missing-role',
        message: 'Interactive element missing role attribute',
        element: element
      });
    }

    // Check color contrast for text elements
    if (element.textContent && element.textContent.trim()) {
      const style = window.getComputedStyle(element);
      const color = style.color;
      const backgroundColor = style.backgroundColor;

      if (color && backgroundColor && color !== backgroundColor) {
        const compliance = contrastHelpers.checkWCAGCompliance(color, backgroundColor, false);
        if (!compliance.passes) {
          issues.push({
            type: 'poor-contrast',
            message: 'Text contrast ratio ' + compliance.ratio.toFixed(2) + ' does not meet WCAG AA standards',
            element: element,
            ratio: compliance.ratio
          });
        }
      }
    }

    return issues;
  },

  /**
   * Run accessibility audit on page
   * @param {HTMLElement} container - Container to audit (default: document.body)
   * @returns {Object} Audit results
   */
  auditPage: function(container) {
    if (!browserHelpers.isBrowser()) {
      return { supported: false, issues: [] };
    }

    container = container || document.body;
    const allElements = container.querySelectorAll('*');
    let allIssues = [];

    Array.from(allElements).forEach(function(element) {
      const issues = a11yTesting.checkAccessibility(element);
      allIssues = allIssues.concat(issues);
    });

    // Group issues by type
    const groupedIssues = {};
    allIssues.forEach(function(issue) {
      if (!groupedIssues[issue.type]) {
        groupedIssues[issue.type] = [];
      }
      groupedIssues[issue.type].push(issue);
    });

    return {
      supported: true,
      totalIssues: allIssues.length,
      issues: allIssues,
      groupedIssues: groupedIssues,
      timestamp: new Date().toISOString()
    };
  }
};

/**
 * Initialize accessibility features
 * @param {Object} options - Accessibility options
 * @returns {Object} Initialization result
 */
export function initializeAccessibility(options) {
  options = options || {};

  if (!browserHelpers.isBrowser()) {
    return {
      enabled: false,
      reason: 'Not in browser environment'
    };
  }

  const features = {
    reducedMotion: motionHelpers.prefersReducedMotion(),
    highContrast: contrastModeHelpers.isHighContrastMode(),
    screenReader: !!navigator.userAgent.match(/NVDA|JAWS|VoiceOver|TalkBack/i)
  };

  // Set up live region for announcements
  ariaHelpers.announceToScreenReader('', 'polite');

  // Log accessibility features in development
  if (process.env.NODE_ENV === 'development') {
    console.log('♿ Accessibility initialized:', features);

    // Run accessibility audit if enabled
    if (options.runAudit) {
      setTimeout(function() {
        const audit = a11yTesting.auditPage();
        if (audit.totalIssues > 0) {
          console.warn('⚠️ Accessibility issues found:', audit.groupedIssues);
        } else {
          console.log('✅ No accessibility issues found');
        }
      }, 1000);
    }
  }

  return {
    enabled: true,
    features: features,
    timestamp: new Date().toISOString()
  };
}

/**
 * Accessibility Event Handlers
 */
export const a11yEventHandlers = {
  /**
   * Create accessible click handler (supports Enter and Space keys)
   * @param {Function} onClick - Click handler function
   * @returns {Object} Event handlers for click and keydown
   */
  createAccessibleClickHandler: function(onClick) {
    if (!onClick || typeof onClick !== 'function') {
      return {};
    }

    return {
      onClick: onClick,
      onKeyDown: function(event) {
        if (keyboardHelpers.isKey(event, keyboardHelpers.KEYS.ENTER) ||
            keyboardHelpers.isKey(event, keyboardHelpers.KEYS.SPACE)) {
          event.preventDefault();
          onClick(event);
        }
      }
    };
  },

  /**
   * Create accessible form submission handler
   * @param {Function} onSubmit - Submit handler function
   * @returns {Function} Form submit handler
   */
  createAccessibleSubmitHandler: function(onSubmit) {
    if (!onSubmit || typeof onSubmit !== 'function') {
      return function() {};
    }

    return function(event) {
      event.preventDefault();

      // Announce form submission to screen readers
      const language = localStorage.getItem('arbiwin_language') || 'bg';
      const message = language === 'bg' ? 'Формата се изпраща' : 'Form is being submitted';

      screenReaderHelpers.announceStatus(message, language, 'assertive');

      onSubmit(event);
    };
  },

  /**
   * Create accessible modal handlers
   * @param {Function} onClose - Close modal function
   * @param {Function} onConfirm - Confirm action function
   * @returns {Object} Modal event handlers
   */
  createModalHandlers: function(onClose, onConfirm) {
    return {
      onKeyDown: function(event) {
        keyboardHelpers.handleModalNavigation(event, onClose, onConfirm);
      },

      onBackdropClick: function(event) {
        if (event.target === event.currentTarget && onClose) {
          onClose();
        }
      }
    };
  }
};

/**
 * Export all accessibility utilities
 */
export default {
  aria: ariaHelpers,
  keyboard: keyboardHelpers,
  focus: focusHelpers,
  screenReader: screenReaderHelpers,
  contrast: contrastHelpers,
  motion: motionHelpers,
  contrastMode: contrastModeHelpers,
  testing: a11yTesting,
  eventHandlers: a11yEventHandlers,
  initialize: initializeAccessibility
};
